import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { NumberParam, withDefault, StringParam, useQueryParams, NumericArrayParam } from 'use-query-params';

import { debounce } from 'utils/debounce';

import { setTableSizeAction } from '../store/user';
import { accountIdSelector } from '../store/user/selectors';
import api from '../utils/axios';

export default (params = {}) => {
  const [queryParams, setQueryParams] = useQueryParams({
    order: StringParam,
    orderBy: StringParam,
    q: withDefault(StringParam, undefined),
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, params.defaultLimit || 10),
    consultantIds: NumericArrayParam,
  });
  const [localQ, setLocalQ] = useState(queryParams.q);
  const accountId = useSelector(accountIdSelector);
  const dispatch = useDispatch();

  const updateQueryParams = useCallback(
    (newState) => {
      setQueryParams((prevState) => ({ ...prevState, page: 1, ...newState }));
    },
    [setQueryParams],
  );
  const updateQ = useCallback((q) => updateQueryParams({ q }), [updateQueryParams]);
  const updateSort = useCallback(
    (newOrderBy, order) => {
      updateQueryParams({
        order: order || undefined,
        orderBy: order ? newOrderBy : undefined,
      });
    },
    [updateQueryParams],
  );
  const updatePage = useCallback(
    (page) => {
      updateQueryParams({ page });
    },
    [updateQueryParams],
  );

  const updateLimit = useCallback(
    (limit) => {
      api.patch(`/acc/${accountId}/table_size_settings`, { table_size: limit }).then((res) => res.data);
      dispatch(setTableSizeAction(limit));
      updateQueryParams({ limit });
    },
    [dispatch, accountId, updateQueryParams],
  );

  const setQDebounced = useCallback(
    debounce((value, qParam) => {
      if (value === qParam) {
        return;
      }

      updateQ(value);
    }, 250),
    [],
  );

  useEffect(() => {
    setQDebounced(localQ, queryParams.q);
  }, [localQ, queryParams.q, setQDebounced]);

  useEffect(() => {
    if (queryParams.q === localQ) {
      return;
    }

    setLocalQ(queryParams.q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.q]);

  return {
    setQ: setLocalQ,
    setSort: updateSort,
    setPage: updatePage,
    setLimit: updateLimit,
    queryParams,
    q: localQ,
    updateQueryParams,
  };
};
